import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';

const ToastContext = createContext(null);

/**
 * Avisos curtos. "Salvo" só é mostrado depois que o servidor confirma a operação
 * (quem chama usa toast.success no onSuccess da mutação).
 */
export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => setItems((xs) => xs.filter((t) => t.id !== id)), []);
  const push = useCallback(
    (type, message, { action, duration } = {}) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-2), { id, type, message, action }]);
      setTimeout(() => dismiss(id), duration ?? (action ? 7000 : type === 'error' ? 6000 : 3500));
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      info: (m, o) => push('info', m, o),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.type}`} role={t.type === 'error' ? 'alert' : 'status'}>
            {t.type === 'success' ? (
              <CheckCircle2 aria-hidden="true" />
            ) : t.type === 'error' ? (
              <AlertCircle aria-hidden="true" />
            ) : (
              <Info aria-hidden="true" />
            )}
            <span className="toast-msg">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
